import { createContext, type CSSProperties, type ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { rhythmLabel } from "../../shared/live-series";
import { labelParts } from "../../shared/live-time";
import type { WebinarDetail, WebinarSummary } from "../../shared/types";
import { Icon } from "../components/ui/Icon";
import { api } from "../lib/api";
import { SAMPLE_ART } from "../lib/sample-art";
import { type Colours, previewVars } from "./looks";

/**
 * The business's own pages drawn in its colours, for setup and Settings: its sign-up page, its room and an email.
 * They carry the business's real name, logo and host, and its newest room when it has one; otherwise the sample
 * words a first room replaces.
 */
export interface PreviewWho {
  business: string;
  logoUrl: string | null;
  hostName: string;
  hostPhoto: string | null;
  /** The address the browser bar shows. */
  address: string;
  fromName: string;
  fromEmail: string | null;
  footer: string;
  room: PreviewRoom | null;
}

export interface PreviewRoom {
  title: string;
  slug: string;
  description: string | null;
  live: boolean;
  /** "Free live class · Every Tuesday", "Free class · Watch any time". */
  eye: string;
  /** "Tuesday 3 November", or "Watch any time". */
  date: string;
  /** "6:00 PM, 48 minutes", or "41 minutes, starts when you press play". */
  time: string;
  /** How long it runs: a live's planned length, a recording's own; 0 while it is not known. */
  minutes: number;
  registered: number;
  poster: string | null;
}

const minutesWords = (n: number) => `${n} minute${n === 1 ? "" : "s"}`;

/**
 * What the sign-up page says of a room, from its own records: a live's next session and planned length (and its
 * rhythm, for one that repeats), or a recording's length.
 */
export function previewRoomOf(w: WebinarSummary, d: Pick<WebinarDetail, "title" | "slug" | "description" | "live">): PreviewRoom {
  const session = w.live;
  const base = { title: d.title, slug: d.slug, description: d.description, registered: w.registered, poster: w.poster_url };
  if (w.schedule_mode !== "on_demand" && session) {
    const at = labelParts(session.startsAt, session.timezone, "long");
    const rule = d.live?.series?.rule;
    const minutes = session.lengthMin;
    return {
      ...base,
      live: true,
      eye: rule ? `Free live class · ${rhythmLabel(rule)}` : "Free live class",
      date: at ? `${at.weekday} ${at.day} ${at.month}` : w.schedule_label,
      time: [at?.time, minutes ? minutesWords(minutes) : ""].filter(Boolean).join(", "),
      minutes,
    };
  }
  const minutes = w.duration_sec > 0 ? Math.max(1, Math.round(w.duration_sec / 60)) : 0;
  return {
    ...base,
    live: false,
    eye: "Free class · Watch any time",
    date: "Watch any time",
    time: minutes ? `${minutesWords(minutes)}, starts when you press play` : "Starts when you press play",
    minutes,
  };
}

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "F";

/** A published room with its words, once: the one picked, or else the newest. What the previews show of it. */
export function usePreviewRoom(pick?: string | null): PreviewRoom | null {
  const [room, setRoom] = useState<PreviewRoom | null>(null);
  useEffect(() => {
    let alive = true;
    api.webinars
      .list()
      .then(async (r) => {
        const published = r.webinars.filter((w) => w.status === "published" && !w.quick_live);
        const chosen: WebinarSummary | undefined = published.find((w) => w.id === pick) ?? published[0];
        if (!chosen) {
          if (alive) setRoom(null);
          return;
        }
        const d = await api.webinars.get(chosen.id);
        if (alive) setRoom(previewRoomOf(chosen, d));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [pick]);
  return room;
}

/** Below this width a preview draws the page the way a phone shows it, rather than a laptop's page shrunk to nothing. */
const NARROW_BOX = 560;
/** The width a page is drawn at then, so the words stay close to their own size. */
const PHONE_CANVAS = 380;
const NarrowContext = createContext(false);
/** Is this preview drawing the phone's layout of the page? */
const useNarrow = () => useContext(NarrowContext);

/** A page drawn at its own width (880), scaled to fit the space it is given; on a phone, at a phone's width. */
export function PreviewFit({ width = 880, maxHeight, children, id }: { width?: number; maxHeight?: number; children: ReactNode; id?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ scale: 1, height: 0, narrow: false });
  useLayoutEffect(() => {
    const el = box.current;
    const inner = canvas.current;
    if (!el || !inner) return;
    const measure = () => {
      const narrow = el.clientWidth < NARROW_BOX;
      const scale = el.clientWidth / (narrow ? Math.min(width, PHONE_CANVAS) : width);
      const h = inner.offsetHeight * scale;
      setFit({ scale, height: maxHeight ? Math.min(h, maxHeight) : h, narrow });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [width, maxHeight]);
  return (
    <div className="pvfit" ref={box} id={id} style={{ height: fit.height }}>
      <div className="pvcanvas" ref={canvas} style={{ width: fit.narrow ? Math.min(width, PHONE_CANVAS) : width, transform: `scale(${fit.scale})` }}>
        <NarrowContext.Provider value={fit.narrow}>{children}</NarrowContext.Provider>
      </div>
    </div>
  );
}

export function BrowserFrame({ url, children }: { url: ReactNode; children: ReactNode }) {
  return (
    <div className={useNarrow() ? "browser narrow" : "browser"}>
      <div className="bb">
        <i />
        <i />
        <i />
        <div className="url">
          <Icon name="lock" />
          <span>{url}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

function Crest({ who, size = 34 }: { who: PreviewWho; size?: number }) {
  return who.logoUrl ? (
    <img src={who.logoUrl} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }} />
  ) : (
    <i style={size !== 34 ? { width: size, height: size } : undefined}>{initialsOf(who.business)}</i>
  );
}

/** The sign-up page as the page designer lays it out by default, in the business's colours. */
export function SignupPreview({ colours, who }: { colours: Colours; who: PreviewWho }) {
  const narrow = useNarrow();
  const r = who.room;
  const g = r
    ? {
        eye: r.eye,
        title: r.title,
        desc: r.description || "",
        date: r.date,
        time: r.time,
        btn: r.live ? "Save my seat" : "Watch now",
        foot: r.live ? "Can’t make it live? Register anyway and the replay lands on your page." : "Your own page keeps your place if you stop halfway.",
      }
    : {
        eye: "Free class",
        title: "Your title",
        desc: "A line about what people will learn. We write it from your talk when you make your first room.",
        date: "Your date and time",
        time: "Set when you make it",
        btn: "Save my seat",
        foot: "Can’t make it live? The replay lands on their page.",
      };
  return (
    <div className="pv" style={previewVars(colours) as CSSProperties}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: narrow ? "18px 20px 4px" : "24px 44px 8px" }}>
        <div className="logo">
          <Crest who={who} />
          {who.business}
        </div>
        <div style={{ fontSize: 14, opacity: 0.65 }}>Free class</div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0,1fr)" : "1.05fr .95fr",
          gap: narrow ? 24 : 40,
          padding: narrow ? "14px 20px 24px" : "22px 44px 44px",
          alignItems: "start",
        }}
      >
        <div style={{ paddingTop: narrow ? 0 : 10 }}>
          <div className="eyeb">{g.eye}</div>
          <h1 className="ph1" style={{ fontSize: narrow ? 24 : 28, lineHeight: 1.2, marginTop: 14 }}>
            {g.title}
          </h1>
          {g.desc ? <p style={{ fontSize: narrow ? 16 : 18, lineHeight: 1.6, marginTop: narrow ? 12 : 18, opacity: 0.82 }}>{g.desc}</p> : null}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 26 }}>
            <img src={who.hostPhoto ?? SAMPLE_ART.host} alt="" style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover" }} />
            <div>
              <b style={{ fontSize: 16 }}>{who.hostName}</b>
              <div style={{ fontSize: 14, opacity: 0.68 }}>Your host</div>
            </div>
          </div>
          {r && r.registered > 0 ? (
            <div style={{ display: "flex", gap: 26, marginTop: 28, fontSize: 14 }}>
              <div>
                <b style={{ display: "block", fontFamily: "var(--pft)", fontSize: 24, color: "var(--pa)" }}>{r.registered.toLocaleString("en-US")}</b>
                <span style={{ opacity: 0.7 }}>registered</span>
              </div>
              {r.minutes ? (
                <div>
                  <b style={{ display: "block", fontFamily: "var(--pft)", fontSize: 24, color: "var(--pa)" }}>{r.minutes} min</b>
                  <span style={{ opacity: 0.7 }}>{r.live ? "with Q&A" : "on demand"}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px -24px rgba(0,0,0,.35)", overflow: "hidden", color: "#2a2224" }}>
          <img src={r?.poster ?? SAMPLE_ART.talk} alt="" style={{ width: "100%", height: 200, objectFit: "cover", objectPosition: "center 30%" }} />
          <div style={{ padding: "20px 22px 22px" }}>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "var(--pft)" }}>{g.date}</div>
            <div style={{ fontSize: 14, opacity: 0.68 }}>{g.time}</div>
            <div className="pin" style={{ marginTop: 16 }}>
              First name
            </div>
            <div className="pin" style={{ marginTop: 10 }}>
              Email
            </div>
            <div className="pbtn" style={{ width: "100%", marginTop: 14 }}>
              {g.btn}
            </div>
            <div style={{ fontSize: 13, opacity: 0.62, marginTop: 12, textAlign: "center", lineHeight: 1.45 }}>{g.foot}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The room, light or in the evening colours, with its chat as it starts. */
export function RoomPreview({ colours, who, dark }: { colours: Colours; who: PreviewWho; dark: boolean }) {
  const narrow = useNarrow();
  const line = dark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.08)";
  const panel = dark ? "rgba(255,255,255,.05)" : "#fff";
  return (
    <div className={dark ? "pv dark" : "pv"} style={previewVars(colours) as CSSProperties}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: narrow ? "14px 16px" : "16px 28px", borderBottom: `1px solid ${line}` }}>
        <div className="logo">
          <Crest who={who} />
        </div>
        <div>
          <b className="ph1" style={{ fontSize: 16 }}>
            {who.room?.title ?? "Your title"}
          </b>
          <div style={{ fontSize: 13, opacity: 0.65 }}>Hosted by <bdi>{who.hostName}</bdi></div>
        </div>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, height: 30, padding: "0 12px", borderRadius: 999, background: "#d92d20", color: "#fff", fontWeight: 700, fontSize: 13 }}>
          ● Live
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0,1fr)" : "1fr 250px", gap: narrow ? 14 : 18, padding: narrow ? "14px 16px 18px" : "20px 28px 26px" }}>
        <div>
          <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", aspectRatio: "16/9", background: "#000" }}>
            <img src={who.room?.poster ?? SAMPLE_ART.talk} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", left: 14, right: 14, bottom: 12, height: 5, borderRadius: 3, background: "rgba(255,255,255,.3)" }}>
              <i style={{ display: "block", width: "52%", height: "100%", borderRadius: 3, background: "var(--pa)" }} />
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", alignItems: "center", gap: narrow ? 12 : 16, padding: "16px 18px", borderRadius: 14, background: panel, border: `1px solid ${line}` }}>
            <div>
              <div className="eyeb">Your offer</div>
              <b className="ph1" style={{ fontSize: 18, display: "block", marginTop: 4 }}>
                What you offer at the end
              </b>
            </div>
            <div className="pbtn" style={{ marginLeft: "auto" }}>
              Your button
            </div>
          </div>
        </div>
        <div style={{ borderRadius: 14, background: panel, border: `1px solid ${line}`, padding: "12px 14px", display: "flex", flexDirection: "column", minHeight: narrow ? 150 : undefined }}>
          <b style={{ fontSize: 14, paddingBottom: 6, borderBottom: `1px solid ${line}` }}>Chat</b>
          <div style={{ fontSize: 13, opacity: 0.6, padding: "12px 0" }}>Questions and hellos show up here as people join.</div>
          <div style={{ marginTop: "auto", height: 38, borderRadius: 9, border: `1px solid ${line}`, display: "flex", alignItems: "center", padding: "0 12px", fontSize: 13, opacity: 0.6 }}>
            Say something…
          </div>
        </div>
      </div>
    </div>
  );
}

interface PreviewMail {
  subject: string;
  title: string;
  body: string;
  button?: string;
  time?: string;
  test?: boolean;
  replyTo?: string;
}

/** An email as it lands: the business's name and address, its colours and its footer. */
export function EmailPreview({ colours, who, mail }: { colours: Colours; who: PreviewWho; mail?: PreviewMail }) {
  const narrow = useNarrow();
  const m: PreviewMail = mail ?? {
    subject: "You’re in. Here’s your watch link",
    title: "You’re in.",
    body: who.room
      ? `${who.room.title}: your seat is saved, and everything you need is on your own page.`
      : "It starts at the time you set. Your seat is saved, and everything you need is on your own page.",
    button: "Open your page",
  };
  const initials = initialsOf(who.business);
  const from = who.fromEmail ?? "your address, once it is set up";
  return (
    <div style={{ ...(previewVars(colours) as CSSProperties), background: "#f1eeec", padding: narrow ? 12 : 24, fontFamily: "var(--pfb)", color: "#222" }}>
      <div style={{ background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
        <div className="pvmh">
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--pa)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 14 }}>{initials}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15 }}>
              <b>{who.fromName}</b> <span style={{ color: "#777", fontSize: 13 }}>&lt;{from}&gt;</span>
            </div>
            <div style={{ fontSize: 13, color: "#777" }}>to {m.test ? "you" : "a guest"}</div>
            <div className="pvmsub">{m.subject}</div>
          </div>
          <div style={{ fontSize: 13, color: "#777" }}>{m.time ?? "9:00 AM"}</div>
        </div>
        <div style={{ maxWidth: 560, margin: narrow ? "14px auto 20px" : "18px auto 26px", padding: narrow ? "0 16px" : "0 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 16 }}>
            <span style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--pa)", color: "#fff", display: "grid", placeItems: "center", fontSize: 12 }}>{initials}</span>
            {who.business}
          </div>
          <h2 style={{ fontWeight: 800, fontSize: 24, lineHeight: 1.25, letterSpacing: "-.02em", marginTop: 22, color: "#1d1a1b" }}>{m.title}</h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, marginTop: 12, color: "#444" }}>{m.body}</p>
          {m.test ? (
            <p style={{ fontSize: 15, color: "#555", marginTop: 16 }}>Replies go to {m.replyTo}.</p>
          ) : (
            <>
              <div style={{ marginTop: 20, display: "inline-flex", height: 46, padding: "0 24px", borderRadius: 10, background: "var(--pa)", color: "#fff", fontWeight: 700, fontSize: 15, alignItems: "center" }}>
                {m.button ?? "Open your page"}
              </div>
              <p style={{ fontSize: 14, color: "#666", marginTop: 16 }}>Can’t make it? The replay will be waiting for you.</p>
            </>
          )}
          <div style={{ borderTop: "1px solid #eee", marginTop: 26, paddingTop: 14, fontSize: 12.5, color: "#888", lineHeight: 1.5, whiteSpace: "pre-line" }}>
            {m.test ? (
              "Sent from Flashroom as a test. Only you got it."
            ) : (
              <>
                {who.footer} <u>Unsubscribe</u>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface InboxRow {
  initials: string;
  colour: string;
  from: string;
  subject: string;
  snippet: string;
  time: string;
  mine?: boolean;
  fresh?: boolean;
}

/** An inbox, to show where the business's emails land among everyone else's. */
export function InboxMock({ rows }: { rows: InboxRow[] }) {
  return (
    <div className={useNarrow() ? "inbox narrow" : "inbox"}>
      <div className="ih">
        <Icon name="inbox" /> Inbox <span style={{ marginLeft: "auto", fontSize: 13, color: "#777", fontWeight: 600 }}>Spam (0)</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className={["it", r.mine ? "hl" : "", r.fresh ? "newin" : ""].filter(Boolean).join(" ")}>
          <div className="av" style={{ background: r.colour }}>
            {r.initials}
          </div>
          <div className="fr">{r.from}</div>
          <div className="sj">
            <b>{r.subject}</b> · {r.snippet}
          </div>
          <div className="tm">{r.time}</div>
        </div>
      ))}
    </div>
  );
}

/** A guest's inbox with the business's two emails in it. */
export function guestInbox(who: PreviewWho, accent: string): InboxRow[] {
  const i = initialsOf(who.business);
  const title = who.room?.title ?? "Your class";
  return [
    { initials: i, colour: accent, from: who.fromName, subject: "We start in an hour", snippet: `${title} begins soon. Your seat is saved.`, time: "5:00 PM", mine: true },
    { initials: "DL", colour: "#5b7a6a", from: "Downtown Library", subject: "Your hold is ready", snippet: "Pick it up by Saturday at the front desk.", time: "3:12 PM" },
    { initials: i, colour: accent, from: who.fromName, subject: "You’re in. Here’s your watch link", snippet: "Everything is on your own page.", time: "Oct 30", mine: true },
    { initials: "L", colour: "#8a6aa8", from: "Lena", subject: "Book club on Thursday?", snippet: "I can bring the lemon cake again.", time: "Oct 31" },
    { initials: "WP", colour: "#4a6a8a", from: "Water and Power", subject: "Your October bill", snippet: "Nothing to do, it pays by itself.", time: "Oct 30" },
  ];
}

/** The host's own inbox, the test landing on top once sent. */
export function hostInbox(who: PreviewWho, accent: string, test: boolean): InboxRow[] {
  const rows: InboxRow[] = [
    { initials: "CA", colour: "#9a6a3a", from: "Calendar", subject: "Tomorrow", snippet: "Three meetings, the first at 9:00 AM.", time: "1:58 PM" },
    { initials: "TC", colour: "#4a6a8a", from: "Title company", subject: "Closing papers are ready", snippet: "Please review before Friday.", time: "11:20 AM" },
    { initials: "BK", colour: "#5b7a6a", from: "Your bank", subject: "Your statement is ready", snippet: "October’s statement is in your account.", time: "9:05 AM" },
    { initials: "CR", colour: "#8a6aa8", from: "County records", subject: "Your saved search", snippet: "4 new sales this week.", time: "Oct 31" },
  ];
  return test
    ? [{ initials: initialsOf(who.business), colour: accent, from: who.fromName, subject: `Test from ${who.fromName}`, snippet: "If you’re reading this, your address works.", time: "Just now", mine: true, fresh: true }, ...rows]
    : rows;
}

type PreviewTab = "signup" | "room" | "email";

/** The big preview: the sign-up page, the room (light or evening) and an email, in the colours being chosen. */
export function PreviewPanel({ colours, who, maxHeight }: { colours: Colours; who: PreviewWho; maxHeight?: number }) {
  const [tab, setTab] = useState<PreviewTab>("signup");
  const [dark, setDark] = useState(false);
  const slug = who.room?.slug ?? "your-room";
  const url = tab === "email" ? "An inbox" : `${who.address}/${tab === "room" ? `${slug}/watch` : slug}`;
  return (
    <>
      <div className="pvbar">
        <div className="seg" role="tablist" aria-label="Preview">
          {(
            [
              ["signup", "Sign-up page"],
              ["room", "The room"],
              ["email", "An email"],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        {tab === "room" ? (
          <div className="seg" role="group" aria-label="The room's colours">
            <button type="button" aria-pressed={!dark} className={!dark ? "on" : ""} onClick={() => setDark(false)}>
              <Icon name="sun" />
              Light
            </button>
            <button type="button" aria-pressed={dark} className={dark ? "on" : ""} onClick={() => setDark(true)}>
              <Icon name="moon" />
              Dark
            </button>
          </div>
        ) : null}
      </div>
      <PreviewFit maxHeight={maxHeight}>
        {tab === "email" ? (
          <EmailPreview colours={colours} who={who} />
        ) : (
          <BrowserFrame url={url}>{tab === "room" ? <RoomPreview colours={colours} who={who} dark={dark} /> : <SignupPreview colours={colours} who={who} />}</BrowserFrame>
        )}
      </PreviewFit>
    </>
  );
}
